import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronDown } from 'lucide-react';
import { DRVRequirements, NutrientRequirement } from '@/types/client';
import { NutrientCard, NutrientList } from './NutrientCard';

interface MicroTargetsSectionProps {
  drvRequirements: DRVRequirements | null;
  micronutrientTargets: any;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  vitaminsSectionOpen: boolean;
  onVitaminsSectionOpenChange: (open: boolean) => void;
  mineralsSectionOpen: boolean;
  onMineralsSectionOpenChange: (open: boolean) => void;
  miscellaneousSectionOpen: boolean;
  onMiscellaneousSectionOpenChange: (open: boolean) => void;
  isMicroLoading?: boolean;
}

const MicroTargetsSection = ({
  drvRequirements,
  micronutrientTargets,
  isOpen,
  onOpenChange,
  vitaminsSectionOpen,
  onVitaminsSectionOpenChange,
  mineralsSectionOpen,
  onMineralsSectionOpenChange,
  miscellaneousSectionOpen,
  onMiscellaneousSectionOpenChange,
  isMicroLoading = false
}: MicroTargetsSectionProps) => {
  const hasVitamins = drvRequirements?.micronutrients 
    ? ((drvRequirements.micronutrients.vitaminsFatSoluble?.length || 0) + 
       (drvRequirements.micronutrients.vitaminsWaterSoluble?.length || 0) > 0)
    : (micronutrientTargets?.vitamins && Object.keys(micronutrientTargets.vitamins).length > 0);

  const hasMinerals = drvRequirements?.micronutrients
    ? ((drvRequirements.micronutrients.mineralsMajor?.length || 0) + 
       (drvRequirements.micronutrients.mineralsTrace?.length || 0) > 0)
    : (micronutrientTargets?.minerals && Object.keys(micronutrientTargets.minerals).length > 0);

  const hasMiscellaneous = drvRequirements?.other
    ? drvRequirements.other.filter(
        (n) => n.nutrientId !== 'water' && n.nutrientId !== 'water_from_drinks'
      ).length > 0
    : (micronutrientTargets?.miscellaneous && Object.keys(micronutrientTargets.miscellaneous).length > 0);

  const hasBioactive = drvRequirements?.bioactive && drvRequirements.bioactive.length > 0;

  const hasAnyMicroNutrients = hasVitamins || hasMinerals || hasMiscellaneous || hasBioactive ||
    (micronutrientTargets && (
      Object.keys(micronutrientTargets.vitamins || {}).length > 0 ||
      Object.keys(micronutrientTargets.minerals || {}).length > 0 ||
      Object.keys(micronutrientTargets.miscellaneous || {}).length > 0
    ));

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <Card className="bg-white border-cs-secondary/30 shadow-sm hover:shadow-md transition-all duration-200 relative overflow-hidden">
        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-cs-secondary to-cs-primary"></div>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/10 transition-colors">
            <CardTitle className="flex items-center justify-between text-gray-900">
              <span className="flex flex-col gap-1">
                <span className="flex items-center gap-2">
                  Micro Targets
                </span>
                {drvRequirements?.fallbackSourcesUsed && drvRequirements.fallbackSourcesUsed.length > 0 && (
                  <span className="text-xs font-normal text-gray-500">
                    Fallback DRV formulas used for specific fields:{" "}
                    {drvRequirements.fallbackSourcesUsed
                      .map((s) => s.replace(/_/g, " ").toUpperCase())
                      .join(", ")}
                  </span>
                )}
                <span className="text-xs font-normal text-gray-500">
                  Hover nutrient headings or values to see which formula each field uses.
                </span>
              </span>
              <ChevronDown 
                className={`h-4 w-4 transition-transform duration-200 text-cs-secondary ${isOpen ? 'rotate-180' : ''}`} 
              />
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-6">
            {/* Vitamins */}
            {hasVitamins && (
              <Collapsible open={vitaminsSectionOpen} onOpenChange={onVitaminsSectionOpenChange}>
                <Card className="bg-white border-cs-primary/20 shadow-sm hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-4">
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between cursor-pointer">
                        <CardTitle className="text-lg text-gray-900">Vitamins</CardTitle>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${vitaminsSectionOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="space-y-6">
                   
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {drvRequirements?.micronutrients ? (() => {
                          const allVitamins: NutrientRequirement[] = [
                            ...(drvRequirements.micronutrients.vitaminsFatSoluble || []),
                            ...(drvRequirements.micronutrients.vitaminsWaterSoluble || [])
                          ];
                          
                          return allVitamins.length > 0 ? allVitamins.map((nutrient: NutrientRequirement) => (
                            <NutrientCard 
                              key={nutrient.nutrientId}
                              nutrient={nutrient}
                              borderColor="border-cs-primary/20"
                            />
                          )) : (
                            <div className="text-center py-8 col-span-full">
                              <p className="text-gray-500">No vitamins available</p>
                            </div>
                          );
                        })() : (
                          // Fallback to old micronutrientTargets structure
                          micronutrientTargets?.vitamins ? (
                            <NutrientList 
                              nutrients={Object.entries(micronutrientTargets.vitamins)}
                              emptyMessage="No vitamins available"
                              borderColor="border-cs-primary/20"
                            />
                          ) : (
                            <div className="text-center py-8 col-span-full">
                              <p className="text-gray-500">No vitamins available</p>
                            </div>
                          )
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Minerals */}
            {hasMinerals && (
              <Collapsible open={mineralsSectionOpen} onOpenChange={onMineralsSectionOpenChange}>
                <Card className="bg-white border-cs-secondary/20 shadow-sm hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-4">
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between cursor-pointer">
                        <CardTitle className="text-lg text-gray-900">Minerals</CardTitle>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mineralsSectionOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="space-y-6">
                     
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {drvRequirements?.micronutrients ? (() => {
                          const allMinerals: NutrientRequirement[] = [
                            ...(drvRequirements.micronutrients.mineralsMajor || []),
                            ...(drvRequirements.micronutrients.mineralsTrace || [])
                          ];
                          
                          return allMinerals.length > 0 ? allMinerals.map((nutrient: NutrientRequirement) => (
                            <NutrientCard 
                              key={nutrient.nutrientId}
                              nutrient={nutrient}
                              borderColor="border-cs-secondary/20"
                            />
                          )) : (
                            <div className="text-center py-8 col-span-full">
                              <p className="text-gray-500">No minerals available</p>
                            </div>
                          );
                        })() : (
                          // Fallback to old micronutrientTargets structure
                          micronutrientTargets?.minerals ? (
                            <NutrientList 
                              nutrients={Object.entries(micronutrientTargets.minerals)}
                              emptyMessage="No minerals available"
                              borderColor="border-cs-secondary/20"
                            />
                          ) : (
                            <div className="text-center py-8 col-span-full">
                              <p className="text-gray-500">No minerals available</p>
                            </div>
                          )
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Miscellaneous */}
            {hasMiscellaneous && (
              <Collapsible open={miscellaneousSectionOpen} onOpenChange={onMiscellaneousSectionOpenChange}>
                <Card className="bg-white border-cs-primary/20 shadow-sm hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-4">
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between cursor-pointer">
                        <CardTitle className="text-lg text-gray-900">Miscellaneous</CardTitle>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${miscellaneousSectionOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="space-y-6">
                     
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {drvRequirements?.other ? (() => {
                          // Filter out water (already shown in macros)
                          const otherNutrients = drvRequirements.other.filter(
                            (nutrient) => nutrient.nutrientId !== 'water' && nutrient.nutrientId !== 'water_from_drinks'
                          );
                          
                          return otherNutrients.length > 0 ? otherNutrients.map((nutrient: NutrientRequirement) => (
                            <NutrientCard 
                              key={nutrient.nutrientId}
                              nutrient={nutrient}
                              borderColor="border-cs-primary/20"
                            />
                          )) : (
                            <div className="text-center py-8 col-span-full">
                              <p className="text-gray-500">No other nutrients available</p>
                            </div>
                          );
                        })() : (
                          // Fallback to old micronutrientTargets structure
                          micronutrientTargets?.miscellaneous ? (
                            <NutrientList 
                              nutrients={Object.entries(micronutrientTargets.miscellaneous)}
                              emptyMessage="No other nutrients available"
                              borderColor="border-cs-primary/20"
                            />
                          ) : (
                            <div className="text-center py-8 col-span-full">
                              <p className="text-gray-500">No other nutrients available</p>
                            </div>
                          )
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Bioactive Compounds */}
            {hasBioactive && (
              <Collapsible open={miscellaneousSectionOpen} onOpenChange={onMiscellaneousSectionOpenChange}>
                <Card className="bg-white border-cs-primary/20 shadow-sm hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-4">
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between cursor-pointer">
                        <CardTitle className="text-lg text-gray-900">Bioactive Compounds</CardTitle>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${miscellaneousSectionOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="space-y-6"> 
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {drvRequirements.bioactive.map((nutrient: NutrientRequirement) => (
                          <NutrientCard 
                            key={nutrient.nutrientId}
                            nutrient={nutrient}
                            borderColor="border-cs-primary/20"
                          />
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* No micronutrients message */}
            {!hasAnyMicroNutrients && (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {isMicroLoading ? "Loading micronutrient targets..." : "No micronutrient targets available"}
                </p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default MicroTargetsSection;

